import { Branch } from "@/types/branch";
import { Product } from "@/types/product";
import { Category } from "@/types/store";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  checkStoreStatus,
  checkOperatingHours,
  calculateNextRefreshInterval,
  calculateNextCategoryRefreshInterval,
} from "@/utils/hooks/timeUtils";
import {
  updateStoreStatus,
  updateLastPollTime,
} from "@/store/features/pollSlice";

export const branchApi = createApi({
  reducerPath: "branchApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL}api/v1/`,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Category"],
  endpoints: (builder) => ({
    getBranches: builder.query<Branch[], void>({
      query: () => "branches",
    }),

    getBranchFront: builder.query<Branch, string>({
      query: (url) => ({
        url: `branch/front/?url=${encodeURIComponent(url)}`,
        method: "GET",
      }),
      async onCacheEntryAdded(
        arg,
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        try {
          let timeoutId: NodeJS.Timeout;
          const result = await cacheDataLoaded;
          const branchData = result.data;
          const branchTimezone = branchData.timezone || "America/Chicago";

          const pollBranch = async () => {
            try {
              const response = await fetch(
                `${
                  import.meta.env.VITE_API_BASE_URL
                }api/v1/branch/front/?url=${encodeURIComponent(arg)}`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                  },
                },
              );
              const newData = await response.json();
              const storeTimezone = newData.timezone || "America/Chicago";
              localStorage.setItem("branchTimezone", storeTimezone);

              // Check store status with timezone
              const newStatus = checkStoreStatus(
                newData.operating_hours ?? [],
                newData.is_open ?? true,
                storeTimezone,
              );

              // Update store status
              dispatch(updateStoreStatus(newStatus));
              dispatch(updateLastPollTime());

              // Calculate next polling interval with timezone
              const nextInterval = calculateNextRefreshInterval(
                newData.operating_hours ?? [],
                newData.is_open ?? true,
                storeTimezone,
              );

              timeoutId = setTimeout(pollBranch, nextInterval);
            } catch (error) {
              console.error("Polling error:", error);
              timeoutId = setTimeout(pollBranch, 60000); // Fallback to 1 minute on error
            }
          };

          // Initial setup with timezone
          const initialStatus = checkStoreStatus(
            branchData.operating_hours ?? [],
            branchData.is_open ?? true,
            branchTimezone,
          );
          dispatch(updateStoreStatus(initialStatus));

          const initialInterval = calculateNextRefreshInterval(
            branchData.operating_hours ?? [],
            branchData.is_open ?? true,
            branchTimezone,
          );
          timeoutId = setTimeout(pollBranch, initialInterval);

          await cacheEntryRemoved;
          clearTimeout(timeoutId);
        } catch (error) {
          console.error("Failed to setup branch polling:", error);
        }
      },
    }),

    getCategoryFront: builder.query<
      Category[],
      { branch: string; branchTimezone?: string }
    >({
      query: ({ branch }) =>
        `category?branch=${branch}&status=Active&front_view=true`,
      transformResponse: (res: Category[], _api, { branchTimezone }) => {
        // Filter categories considering timezone
        return res.filter(
          (category) =>
            !category.category_hours?.length ||
            checkOperatingHours(
              category.category_hours,
              branchTimezone || "America/Chicago",
            ),
        );
      },
      providesTags: ["Category"],
      async onCacheEntryAdded(
        arg,
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        try {
          const categoriesData = await cacheDataLoaded;
          const { branchTimezone = "America/Chicago" } = arg;

          const rerunCategoryQuery = () => {
            dispatch(branchApi.util.invalidateTags(["Category"]));
          };

          // Calculate initial interval with timezone
          const interval = calculateNextCategoryRefreshInterval(
            categoriesData.data,
            branchTimezone,
          );

          const intervalId = setInterval(rerunCategoryQuery, interval);

          await cacheEntryRemoved;
          clearInterval(intervalId);
        } catch (error) {
          console.error("Failed to setup category polling:", error);
        }
      },
    }),

    getProductFront: builder.query<
      Product[],
      {
        branch: string;
        category?: string;
        search: string;
        product_type?: string;
      }
    >({
      query: ({ branch, category, search, product_type }) => {
        let url = `product/?branch=${branch}&name=${search}&front_view=true`;
        if (category) {
          url += `&category=${category}`;
        }
        if (product_type) {
          url += `&product_type=${product_type}`;
        }
        return url;
      },
    }),
  }),
});

export const {
  useGetBranchesQuery,
  useGetBranchFrontQuery,
  useLazyGetBranchFrontQuery,
  useGetCategoryFrontQuery,
  useGetProductFrontQuery,
  useLazyGetProductFrontQuery,
} = branchApi;
